import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Button as MuiButton,
  Chip,
  LinearProgress,
  Tooltip,
  Stack,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import { Button } from "../../components/common/button";
import { Loader } from "../../components/loader";
import { adminActions } from "../../store/admin";
import { alertsActions } from "../../store/alerts";
import {
  selectCustomerData,
  selectAdminLoading,
} from "../../store/admin/selectors";
import { selectUserData } from "../../store/user/selectors";
import { Client } from "../../types/Client";
import { ClientDialog } from "../../components/clients";
import { ClientsPageWrapper, Toolbar, Title } from "./styles";

const ClientsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const customerData = useSelector(selectCustomerData);
  const loading = useSelector(selectAdminLoading);
  const userData = useSelector(selectUserData);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(
    null
  );
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");

  useEffect(() => {
    dispatch(adminActions.getCustomerData());
  }, [dispatch]);

  // Helper function to check if subscription is expiring soon (within 7 days)
  const isSubscriptionExpiringSoon = (endDate: string): boolean => {
    const end = new Date(endDate);
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return end <= sevenDaysFromNow && end > now;
  };

  // Helper function to check if subscription is expired
  const isSubscriptionExpired = (endDate: string): boolean => {
    const end = new Date(endDate);
    const now = new Date();
    return end < now;
  };

  // Helper function to calculate quota percentage
  const calculateQuotaPercentage = (used: number, total: number): number => {
    if (total === 0) return 0;
    return Math.min((used / total) * 100, 100);
  };

  const handleAddClient = () => {
    setEditingCustomerId(null);
    setEditingClient(null);
    setDialogOpen(true);
  };

  const handleEditClient = (customerId: string, client: Client) => {
    setEditingCustomerId(customerId);
    setEditingClient(client);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCustomerId(null);
    setEditingClient(null);
  };

  const handleSaveClient = (
    customerNameOrId: string,
    client: Partial<Client>
  ) => {
    if (editingCustomerId) {
      // Update existing customer using _id
      dispatch(
        adminActions.updateClient({ customerId: editingCustomerId, client })
      );
    } else {
      // Add new customer using customer_name
      dispatch(
        adminActions.addClient({
          customerName: customerNameOrId,
          client: client as Omit<Client, "_id" | "subscription">,
        })
      );
    }
    handleCloseDialog();
  };

  const handleToggleStatus = (customerId: string, currentActive: boolean) => {
    // Prevent the logged-in customer from toggling their own status
    console.log("userData:", userData);
    console.log("customerId to toggle:", customerId);
    console.log("currentActive:", currentActive);

    if (userData?.customer_id === customerId) {
      dispatch(
        alertsActions.enqueue({
          sevirity: "error",
          text:
            t("CANNOT_TOGGLE_OWN_STATUS") ||
            "You cannot change your own account status",
        })
      );
      return;
    }

    // Toggle to opposite of current status
    dispatch(
      adminActions.toggleClientStatus({ customerId, active: !currentActive })
    );
  };

  const clients = customerData || [];

  // Filter clients based on status
  const filteredClients = clients.filter((client) => {
    if (filterStatus === "all") return true;
    if (filterStatus === "active") return client.subscription?.active !== false;
    if (filterStatus === "inactive")
      return client.subscription?.active === false;
    return true;
  });

  if (loading && !customerData) {
    return <Loader />;
  }

  return (
    <ClientsPageWrapper>
      <Toolbar>
        <Button label={t("ADD_CUSTOMER")} onClick={handleAddClient} />
        <Title>{t("CLIENTS_MANAGEMENT")}</Title>
      </Toolbar>

      <Box
        sx={{
          padding: {
            xs: "0.5rem",
            sm: "1rem",
            md: "1rem 2rem",
            lg: "1rem 3rem",
          },
          maxWidth: "100%",
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* Status Filter */}
        <Box
          sx={{
            marginBottom: 3,
            display: "flex",
            gap: { xs: 0.5, sm: 1, md: 1.5 },
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <MuiButton
            variant={filterStatus === "all" ? "contained" : "outlined"}
            onClick={() => setFilterStatus("all")}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              paddingX: { xs: 1.5, sm: 2, md: 3 },
              paddingY: { xs: 0.5, sm: 0.75, md: 1 },
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              fontWeight: filterStatus === "all" ? 600 : 400,
              boxShadow:
                filterStatus === "all"
                  ? "0 2px 8px rgba(25, 118, 210, 0.3)"
                  : "none",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
              },
            }}
          >
            {t("ALL")} ({clients.length})
          </MuiButton>
          <MuiButton
            variant={filterStatus === "active" ? "contained" : "outlined"}
            onClick={() => setFilterStatus("active")}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              paddingX: { xs: 1.5, sm: 2, md: 3 },
              paddingY: { xs: 0.5, sm: 0.75, md: 1 },
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              fontWeight: filterStatus === "active" ? 600 : 400,
              boxShadow:
                filterStatus === "active"
                  ? "0 2px 8px rgba(46, 125, 50, 0.3)"
                  : "none",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(46, 125, 50, 0.2)",
              },
            }}
            color="success"
          >
            {t("ACTIVE")} (
            {clients.filter((c) => c.subscription?.active !== false).length})
          </MuiButton>
          <MuiButton
            variant={filterStatus === "inactive" ? "contained" : "outlined"}
            onClick={() => setFilterStatus("inactive")}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              paddingX: { xs: 1.5, sm: 2, md: 3 },
              paddingY: { xs: 0.5, sm: 0.75, md: 1 },
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              fontWeight: filterStatus === "inactive" ? 600 : 400,
              boxShadow:
                filterStatus === "inactive"
                  ? "0 2px 8px rgba(211, 47, 47, 0.3)"
                  : "none",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(211, 47, 47, 0.2)",
              },
            }}
            color="error"
          >
            {t("INACTIVE")} (
            {clients.filter((c) => c.subscription?.active === false).length})
          </MuiButton>
        </Box>

        {filteredClients.length === 0 ? (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", marginTop: 4 }}
          >
            {t("NO_CLIENTS_FOUND")}
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              overflowX: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: 2,
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <Table sx={{ minWidth: { xs: 800, sm: 900, md: 1000, lg: 1200 } }}>
              <colgroup>
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      px: { xs: 1, sm: 2, md: 3 },
                      py: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    }}
                  >
                    {t("CUSTOMER_NAME")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      px: { xs: 1, sm: 2, md: 3 },
                      py: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    }}
                  >
                    {t("EMAIL") || "Email"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      px: { xs: 1, sm: 2, md: 3 },
                      py: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    }}
                  >
                    {t("STATUS")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      px: { xs: 1, sm: 2, md: 3 },
                      py: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    }}
                  >
                    {t("QUOTA_USAGE")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      px: { xs: 1, sm: 2, md: 3 },
                      py: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    }}
                  >
                    {t("SUBSCRIPTION_END")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      px: { xs: 1, sm: 2, md: 3 },
                      py: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    }}
                  >
                    {t("CREATED_AT") || "Created"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      px: { xs: 1, sm: 2, md: 3 },
                      py: 2,
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    }}
                  >
                    {t("ACTIONS")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.map((client) => {
                  const subscription = client.subscription;
                  const isActive = subscription?.active !== false;
                  const quotaPercentage = subscription
                    ? calculateQuotaPercentage(
                        subscription.used_quota,
                        subscription.monthly_quota
                      )
                    : 0;
                  const isExpiringSoon = subscription?.end_date
                    ? isSubscriptionExpiringSoon(subscription.end_date)
                    : false;
                  const isExpired = subscription?.end_date
                    ? isSubscriptionExpired(subscription.end_date)
                    : false;
                  const isLoggedInUser = userData?.customer_id === client._id;

                  return (
                    <TableRow
                      key={client._id}
                      sx={{
                        backgroundColor: !isActive ? "#fafafa" : "inherit",
                        opacity: !isActive ? 0.7 : 1,
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{
                          px: { xs: 1, sm: 2, md: 3 },
                          py: 2,
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.875rem",
                            md: "1rem",
                          },
                        }}
                      >
                        {client.customer_name}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          px: { xs: 1, sm: 2, md: 3 },
                          py: 2,
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.875rem",
                            md: "1rem",
                          },
                        }}
                      >
                        {client.email || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 2 }}
                      >
                        <Chip
                          label={isActive ? t("ACTIVE") : t("INACTIVE")}
                          color={isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 2 }}
                      >
                        {subscription ? (
                          <Box sx={{ minWidth: 150 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      quotaPercentage >= 90
                                        ? "#d32f2f"
                                        : quotaPercentage >= 75
                                        ? "#ed6c02"
                                        : "inherit",
                                    fontWeight:
                                      quotaPercentage >= 90 ? 600 : 400,
                                  }}
                                >
                                  {subscription.used_quota} /{" "}
                                  {subscription.monthly_quota}
                                </Typography>
                                {quotaPercentage >= 90 && (
                                  <Tooltip
                                    title={
                                      subscription.auto_renew
                                        ? "Quota almost full - Auto-renew is ON"
                                        : "Quota almost full - Auto-renew is OFF"
                                    }
                                  >
                                    <WarningIcon
                                      sx={{
                                        fontSize: 16,
                                        color: subscription.auto_renew
                                          ? "#ed6c02"
                                          : "#d32f2f",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    quotaPercentage >= 90
                                      ? "#d32f2f"
                                      : quotaPercentage >= 75
                                      ? "#ed6c02"
                                      : "inherit",
                                  fontWeight: quotaPercentage >= 90 ? 600 : 400,
                                }}
                              >
                                {quotaPercentage.toFixed(0)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={quotaPercentage}
                              sx={{
                                height: 8,
                                borderRadius: 1,
                                backgroundColor: "#e0e0e0",
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor:
                                    quotaPercentage >= 90
                                      ? "#d32f2f"
                                      : quotaPercentage >= 75
                                      ? "#ed6c02"
                                      : "#1976d2",
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          px: { xs: 1, sm: 2, md: 3 },
                          py: 2,
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.875rem",
                            md: "1rem",
                          },
                        }}
                      >
                        {subscription?.end_date ? (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography variant="body2">
                              {new Date(
                                subscription.end_date
                              ).toLocaleDateString()}
                            </Typography>
                            {isExpired && (
                              <Tooltip title="Subscription expired">
                                <WarningIcon color="error" fontSize="small" />
                              </Tooltip>
                            )}
                            {!isExpired && isExpiringSoon && (
                              <Tooltip title="Expires within 7 days">
                                <WarningIcon color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                          </Stack>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          px: { xs: 1, sm: 2, md: 3 },
                          py: 2,
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.875rem",
                            md: "1rem",
                          },
                        }}
                      >
                        {client.created_at
                          ? new Date(client.created_at).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: { xs: 0.5, sm: 1, md: 2 },
                            justifyContent: "flex-end",
                            alignItems: "center",
                            flexWrap: "nowrap",
                          }}
                        >
                          <Tooltip title="Edit Customer">
                            <IconButton
                              onClick={() =>
                                handleEditClient(client._id, client)
                              }
                              color="primary"
                              size="small"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              isLoggedInUser
                                ? t("CANNOT_TOGGLE_OWN_STATUS") ||
                                  "You cannot change your own account status"
                                : isActive
                                ? "Deactivate Customer"
                                : "Activate Customer"
                            }
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: 0.5, sm: 0.75, md: 1 },
                                padding: {
                                  xs: "2px 8px",
                                  sm: "3px 10px",
                                  md: "4px 12px",
                                },
                                borderRadius: "20px",
                                backgroundColor: isLoggedInUser
                                  ? "rgba(0, 0, 0, 0.04)"
                                  : isActive
                                  ? "rgba(46, 125, 50, 0.08)"
                                  : "rgba(158, 158, 158, 0.08)",
                                border: `1px solid ${
                                  isLoggedInUser
                                    ? "rgba(0, 0, 0, 0.12)"
                                    : isActive
                                    ? "rgba(46, 125, 50, 0.3)"
                                    : "rgba(158, 158, 158, 0.3)"
                                }`,
                                transition: "all 0.3s ease",
                                minWidth: {
                                  xs: "90px",
                                  sm: "105px",
                                  md: "120px",
                                },
                                justifyContent: "space-between",
                                opacity: isLoggedInUser ? 0.4 : 1,
                                cursor: isLoggedInUser
                                  ? "not-allowed"
                                  : "default",
                                pointerEvents: isLoggedInUser ? "none" : "auto",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: isLoggedInUser
                                    ? "#9e9e9e"
                                    : isActive
                                    ? "#2e7d32"
                                    : "#9e9e9e",
                                  fontSize: {
                                    xs: "0.65rem",
                                    sm: "0.7rem",
                                    md: "0.75rem",
                                  },
                                  flex: "1",
                                }}
                              >
                                {isActive ? t("ACTIVE") : t("INACTIVE")}
                              </Typography>
                              <Switch
                                checked={isActive}
                                onChange={() =>
                                  handleToggleStatus(client._id, isActive)
                                }
                                disabled={isLoggedInUser}
                                size="small"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "#2e7d32",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                      backgroundColor: "#2e7d32",
                                    },
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ClientDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveClient}
        customerId={editingCustomerId}
        client={editingClient}
      />
    </ClientsPageWrapper>
  );
};

export default ClientsPage;
